import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {IconName} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../../../../libs/shared/ui/src/styles/paletteModel';
import {DraftSummaryModel} from '../../../../../models/api/draftSummaryModel';
import {MessageStatusesEnum} from '../../../../../models/api/messageModel';
import {useIsDraftSummaryVisuallyShared} from '../../../../../store/data/messages/messagesSelectors';
import {CellDraftIcon} from './cellDraftIcon';
import {CellSummary} from './cellSummary';

/*
 * Props.
 */

interface CellDraftProps {
  draft: DraftSummaryModel;
  isSelected?: boolean;
  hasAutoShareDrafts?: boolean;
  isInDraftsFolder?: boolean;
  conversationId?: number;
}

/*
 * Style.
 */

const StyledDraftDiv = styled.div`
  grid-area: bottom-line;
  display: flex;
  align-items: center;
  overflow: hidden;
`;

interface IndicatorProps {
  $isSelected?: boolean;
  $color: PaletteColorsEnum;
}

const StyledIndicatorDiv = styled.div<IndicatorProps>`
  margin-right: 5px;
  flex-shrink: 0;

  color: ${(p) => (p.$isSelected ? p.theme.invertedText : p.theme.palette[p.$color].shade40)};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
`;

/*
 * Component.
 */

export const CellDraft: FC<CellDraftProps> = ({
  draft,
  isSelected,
  hasAutoShareDrafts,
  isInDraftsFolder,
  conversationId,
}) => {
  const isVisuallyShared = useIsDraftSummaryVisuallyShared(draft, conversationId);
  // If the draft failed to send, display a warning.
  if (draft.status === MessageStatusesEnum.DRAFT_FAILED) {
    return (
      <>
        <CellDraftIcon isSelected={isSelected} iconName="warningFilledSmall" color={PaletteColorsEnum.RED} />
        <StyledDraftDiv>
          <StyledIndicatorDiv $isSelected={isSelected} $color={PaletteColorsEnum.RED}>
            <Trans id="cell-draft-failedtosend">Message failed to send</Trans>
          </StyledIndicatorDiv>
        </StyledDraftDiv>
      </>
    );
  }

  // If the draft is a send later, display the corresponding indicator.
  if (draft.status === MessageStatusesEnum.DRAFT_LATER) {
    return (
      <>
        <CellDraftIcon
          isSelected={isSelected}
          iconName="snoozeFilledSmall"
          color={PaletteColorsEnum.ORANGE}
        />
        <StyledDraftDiv>
          <StyledIndicatorDiv $isSelected={isSelected} $color={PaletteColorsEnum.ORANGE}>
            <Trans id="cell-sendlater">Send Later</Trans>
          </StyledIndicatorDiv>
          <CellSummary isSelected={isSelected} summary={draft.blurb} />
        </StyledDraftDiv>
      </>
    );
  }

  // If the draft is pending, display an outbox indicator and the draft summary.
  if (draft.status === MessageStatusesEnum.DRAFT_PENDING) {
    return (
      <>
        <CellDraftIcon isSelected={isSelected} iconName="outboxFilledSmall" color={PaletteColorsEnum.BLUE} />
        <StyledDraftDiv>
          <StyledIndicatorDiv $isSelected={isSelected} $color={PaletteColorsEnum.BLUE}>
            <Trans id="cell-draft-outbox">Outbox</Trans>
          </StyledIndicatorDiv>
          <CellSummary isSelected={isSelected} summary={draft.blurb} />
        </StyledDraftDiv>
      </>
    );
  }

  const renderText = () => {
    if (hasAutoShareDrafts) {
      if (isInDraftsFolder) {
        return isVisuallyShared ? (
          <Trans id="celldraft-shared">Shared</Trans>
        ) : (
          <Trans id="celldraft-personal">Personal</Trans>
        );
      }

      return <Trans id="celldraft-draft">Draft</Trans>;
    }

    return isVisuallyShared ? (
      <Trans id="cell-shareddraft">Shared draft</Trans>
    ) : (
      <Trans id="cell-draft">Draft</Trans>
    );
  };
  const getIconName = (): IconName => {
    if (hasAutoShareDrafts && isInDraftsFolder) {
      return isVisuallyShared ? 'sharedFilledSmall' : 'lockSmall';
    }

    return 'draftFilledSmall';
  };

  // Otherwise, display the draft summary.
  return (
    <>
      <CellDraftIcon isSelected={isSelected} iconName={getIconName()} color={PaletteColorsEnum.BLUE} />
      <StyledDraftDiv>
        <StyledIndicatorDiv $isSelected={isSelected} $color={PaletteColorsEnum.BLUE}>
          {renderText()}
        </StyledIndicatorDiv>
        <CellSummary isSelected={isSelected} summary={draft.blurb} />
      </StyledDraftDiv>
    </>
  );
};
